// HourlyMetricGraph.tsx
import React, { useMemo, useCallback } from 'react';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

interface DataPoint {
  hour_start: string; // ISO string
  count: number; // value to plot
}

interface HourlyMetricGraphProps {
  /** Hourly-bucketed data array. */
  data: DataPoint[];
  /** Human-readable metric name (axis label, legend, tooltip). */
  metricName?: string;
  /** Height of the chart in pixels (default 300). */
  height?: number;
  /** Line stroke color (default '#8884d8'). */
  lineColor?: string;
  /** Force-show legend even with one series (default false). */
  showLegend?: boolean;
}

const TIME_FMT = new Intl.DateTimeFormat('en-US', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false
});

const FULL_FMT = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'medium',
  timeStyle: 'short'
});

function HourlyMetricGraph({
  data,
  metricName = 'Count',
  height = 300,
  lineColor = '#8884d8',
  showLegend = false
}: HourlyMetricGraphProps) {
  const chartData = useMemo(
    () =>
      data.map(({ hour_start, count }) => ({
        hourLabel: TIME_FMT.format(new Date(hour_start)),
        count,
        fullDateTime: FULL_FMT.format(new Date(hour_start))
      })),
    [data]
  );

  const tooltipFormatter = useCallback(
    (value: number) => [value, metricName] as [number, string],
    [metricName]
  );
  const labelFormatter = useCallback(
    (_label: string, payload: any[]) =>
      payload?.[0] ? `Time: ${payload[0].payload.fullDateTime}` : 'Time: –',
    []
  );

  if (chartData.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        No {metricName.toLowerCase()} data to display
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={chartData} margin={{ top: 10, right: 30, bottom: 10, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="hourLabel" tick={{ fontSize: 12 }} angle={-45} textAnchor="end" height={60} />
        <YAxis label={{ value: metricName, angle: -90, position: 'insideLeft' }} allowDecimals={false} />
        <Tooltip formatter={tooltipFormatter} labelFormatter={labelFormatter} />
        {(showLegend || false) && <Legend />}
        <Line
          type="monotone"
          dataKey="count"
          stroke={lineColor}
          strokeWidth={2}
          dot={{ fill: lineColor, strokeWidth: 2, r: 4 }}
          name={metricName}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

export default HourlyMetricGraph;
